import React, {useReducer, useState} from 'react'
import {makeStyles, TableCell, TableRow} from '@material-ui/core';
import PropTypes from 'prop-types';
import Tr from 'translations/Translations';
import moment from "libs/moment";
import WorkableIconDeleteButton from 'components/button/WorkableIconDeleteButton';
import WorkableIconDownloadButton from 'components/button/WorkableIconDownloadButton';
import WorkableIconDownloadCountButton from "components/button/WorkableIconDownloadCountButton";
import DateFormats from "constants/DateFormats";
import iconWorksReducer from "libs/iconWorksReducer";
import WorkableIconUnpublishButton from "components/button/WorkableIconUnpublishButton";
import WorkableIconPublishButton from "components/button/WorkableIconPublishButton";
import WorkableIconSearchButton from "components/button/WorkableIconSearchButton";
import RepoService from "services/RepoService";
import ShareSelectPopup, {sharePopupData} from "components/popup/ShareSelectPopup";
import useSnackbar from "hooks/useSnackbar";
import WorkableIconShareButton from "components/button/WorkableIconShareButton";
import WorkableIconEjectButton from "components/button/WorkableIconEjectButton";
import HelpComponent from "../../../HelpComponent";


const useStyle = makeStyles(theme => ({
    descriptionTooltip: {
        whiteSpace: 'pre-line'
    },
    buttonColumn: theme.components.table.buttonColumn,
}));

function RepoFileTableRow(
    {
        demandId,
        index,
        row,
        isPublished,
        onOpenDownloadHistory,
        onEditDescriptionFile,
        onSetRepoVisibility,
        showShare,
        onMoveToSiwz
    }) {
    const classes = useStyle();
    const [shareFilePopup, setShareFilePopup] = useState(null);
    const {enqueueSuccessSnackbar} = useSnackbar();

    const [iconWorks, dispatchIconWorks] = useReducer(iconWorksReducer, {
        anyWorks: false,
        download: false,
        delete: false,
        share: false,
    });

    const onDownload = () => {
        dispatchIconWorks({type: 'download', works: true});
        RepoService.getDownloadFile(demandId, row.identity, false)
            .finally(() => dispatchIconWorks({type: 'download', works: false}));
    }

    const onDelete = () => {
        dispatchIconWorks({type: 'delete', works: true});
        RepoService.deleteItem(demandId, row.identity)
            .finally(() => {
                dispatchIconWorks({type: 'delete', works: false})
            });
    }

    const onShareFile = (row) => setShareFilePopup(sharePopupData(row.name, row));

    const doShare = (row, system) => {
        setShareFilePopup(null);
        dispatchIconWorks({type: 'share', works: true});
        RepoService.shareRepo(demandId, row.identity, system).then((resp) => {
            if (resp)
                enqueueSuccessSnackbar(Tr("int_upload_success_x", {x: system}, true))
        }).finally(() => {
            dispatchIconWorks({type: 'share', works: false})
        });
    }

    return (
        <TableRow>
            <TableCell>{index}</TableCell>
            <TableCell>
                {row.name}
            </TableCell>
            <TableCell className={classes.buttonColumn}>
                <WorkableIconSearchButton
                    onClick={() => onEditDescriptionFile(row)}
                    tooltip={row.description ? <p className={classes.descriptionTooltip}>{row.description}</p> : <i>{Tr('no_description')}</i>}
                    disabled={iconWorks.anyWorks}
                />
            </TableCell>
            <TableCell>{Tr(`repo.visibility.${row.visibilityStatus}`)}</TableCell>
            <TableCell><HelpComponent text={`${row.fileSize}, SHA256: ${row.sha256}`}></HelpComponent></TableCell>
            <TableCell>{moment(row.createDate).format(DateFormats.DATETIME_FORMAT)}</TableCell>
            {isPublished && <TableCell>{row.publishDate ? moment(row.publishDate).format(DateFormats.DATETIME_FORMAT) : null}</TableCell>}
            <TableCell className={classes.buttonColumn}>
                <WorkableIconDownloadButton
                    onClick={() => onDownload(row.actions.download, row.name)}
                    hideTooltip={!row.actions.download}
                    disabled={iconWorks.anyWorks || !row.actions.download}
                    working={iconWorks.download}
                />
            </TableCell>
            <TableCell className={classes.buttonColumn}>
                <WorkableIconEjectButton
                    tooltip={Tr('move_to_siwz')}
                    onClick={() => onMoveToSiwz([{identity: row.identity, name: row.name}])}
                    hideTooltip={!row.actions.moveToSiwz}
                    disabled={iconWorks.anyWorks || !row.actions.moveToSiwz}
                />
            </TableCell>
            {showShare &&
                <TableCell className={classes.buttonColumn}>
                    <WorkableIconShareButton
                        onClick={() => onShareFile(row)}
                        hideTooltip={!row.actions.download}
                        disabled={iconWorks.anyWorks || !row.actions.download}
                        working={iconWorks.share}
                    />
                    {!!shareFilePopup && <ShareSelectPopup
                        data={shareFilePopup}
                        onCancel={() => setShareFilePopup(null)}
                        onChoose={doShare}
                    />}
                </TableCell>
            }

            <TableCell className={classes.buttonColumn}>
                {row.visibilityStatus === 'STATUS_INTERNAL' ?
                    <WorkableIconPublishButton
                        onClick={() => onSetRepoVisibility(row)}
                        hideTooltip={!row.actions.changeVisibility}
                        disabled={iconWorks.anyWorks || !row.actions.changeVisibility}
                    />
                    :
                    <WorkableIconUnpublishButton
                        onClick={() => onSetRepoVisibility(row)}
                        hideTooltip={!row.actions.changeVisibility}
                        disabled={iconWorks.anyWorks || !row.actions.changeVisibility}
                    />

                }
            </TableCell>

            {isPublished && <TableCell className={classes.buttonColumn}>
                <WorkableIconDownloadCountButton
                    tooltip={Tr("download_count_x", {downloadCount: row.downloadCount})}
                    onClick={() => onOpenDownloadHistory(row)}
                    hideTooltip={!row.actions.downloadDetails}
                    disabled={iconWorks.anyWorks || !row.actions.downloadDetails}
                />
            </TableCell>
            }
            <TableCell className={classes.buttonColumn}>
                <WorkableIconDeleteButton
                    tooltip={row.actions.delete ? Tr('delete') : Tr('delete_repo_disabled')}
                    onClick={onDelete}
                    disabled={iconWorks.anyWorks || !row.actions.delete}
                    working={iconWorks.delete}
                />
            </TableCell>
        </TableRow>
    )
}

RepoFileTableRow.propTypes = {
    demandId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    row: PropTypes.shape({
        identity: PropTypes.number.isRequired
    }).isRequired,
    index: PropTypes.number.isRequired,
    isPublished: PropTypes.bool.isRequired,
    onOpenDownloadHistory: PropTypes.func.isRequired,
    onEditDescriptionFile: PropTypes.func.isRequired,
    onSetRepoVisibility: PropTypes.func.isRequired,
    showShare: PropTypes.bool.isRequired,
};

export default RepoFileTableRow;